import dayjs from "dayjs";

import { Category } from "~/components/category/[id]/quest/type";
import { QuestDurationStatus, pointKindJaLabel } from "~/domains/quest/lib";
import {
  UserQuestProgressQuery,
  UserQuestProgressStatus,
  UserQuestTaskProgressStatus,
} from "~/lib/graphql/client";

const ORDER_TYPE = [
  { value: "start-at", label: "開始日順" },
  { value: "end-at", label: "終了日順" },
  { value: "point", label: "ポイント順" },
] as const;

const QUEST_PROGRESS_STATUSES = [
  { label: "実行前", status: UserQuestProgressStatus.NotRequestedYet },
  { label: "実行中", status: UserQuestProgressStatus.Doing },
  { label: "認証中", status: UserQuestProgressStatus.UnderReviewing },
  { label: "認証済み", status: UserQuestProgressStatus.Verified },
] as const;

type QuestState = (typeof QUEST_PROGRESS_STATUSES)[number];

type QuestOrder = (typeof ORDER_TYPE)[number];

export type { QuestState, QuestOrder };
export { QUEST_PROGRESS_STATUSES, ORDER_TYPE };

type Quest = Category["quests"][number];

type Props = {
  quests: Quest[];
};

type WrappedQuest = Quest & {
  readonly isOpened: boolean;
  readonly isClosed: boolean;
  readonly isNotOpened: boolean;
  readonly durationStatus: QuestDurationStatus;
  readonly progress: {
    readonly id: number;
    readonly status: UserQuestProgressStatus;
    readonly questId: number;
  };
  readonly point: {
    readonly creatorPoint: number;
    readonly curatorPoint: number;
    readonly evangelistPoint: number;
    readonly expertPoint: number;
    readonly supporterPoint: number;
    readonly pointKind:
      | "creatorPoint"
      | "curatorPoint"
      | "evangelistPoint"
      | "expertPoint"
      | "supporterPoint";
    readonly pointKindJaLabel: string;
    readonly pointValue: number;
  };
  readonly tasks: {
    readonly id: number;
    readonly title: string;
    readonly description: string;
    readonly url?: string | null;
    readonly position: number;
    readonly progress: {
      readonly id: number;
      readonly status: UserQuestTaskProgressStatus;
      readonly questTaskId: number;
    };
  }[];
  readonly imageUrl?: string;
};

function wrapQuest(
  quest: Quest,
  currentUserQuestProgresses: UserQuestProgressQuery["currentUserQuestProgresses"],
  currentUserQuestTaskProgresses: UserQuestProgressQuery["currentUserQuestTaskProgresses"]
): WrappedQuest {
  const now = new Date();
  const startAt = dayjs(quest.startAt);
  const closeAt = dayjs(quest.endAt);

  return {
    ...quest,
    get isClosed() {
      return closeAt.isBefore(now);
    },
    get isNotOpened() {
      return startAt.isAfter(now);
    },
    get isOpened() {
      return startAt.isBefore(now) && closeAt.isAfter(now);
    },
    get point() {
      // それぞれのポイント種別を確認し、 0 でないものを抽出する
      const pointKind = (
        [
          "creatorPoint",
          "curatorPoint",
          "evangelistPoint",
          "expertPoint",
          "supporterPoint",
        ] as const
      ).find((kind) => quest[kind] !== 0);

      if (!pointKind) {
        throw new Error("pointKind is not found");
      }

      return {
        creatorPoint: quest.creatorPoint,
        curatorPoint: quest.curatorPoint,
        evangelistPoint: quest.evangelistPoint,
        expertPoint: quest.expertPoint,
        supporterPoint: quest.supporterPoint,
        pointValue:
          quest.creatorPoint +
          quest.curatorPoint +
          quest.evangelistPoint +
          quest.expertPoint +
          quest.supporterPoint,
        pointKind: pointKind,
        pointKindJaLabel: pointKindJaLabel(pointKind),
      };
    },
    get progress() {
      const progress = currentUserQuestProgresses.find(
        (progress) => progress.questId === quest.id
      );
      if (progress) {
        return progress;
      } else {
        return EmptyQuestProgress(quest.id);
      }
    },
    get durationStatus(): QuestDurationStatus {
      if (this.isOpened) {
        return "OPENING";
      } else if (this.isClosed) {
        return "AFTER_PERIOD";
      } else {
        return "BEFORE_PERIOD";
      }
    },
    get tasks() {
      return quest.questTasks
        .map((task) => ({
          id: task.id,
          title: task.title,
          description: task.description,
          url: task.url,
          position: task.position,
          progress:
            currentUserQuestTaskProgresses.find(
              (progress) => progress.questTaskId === task.id
            ) || EmptyQuestTaskProgress(task.id),
        }))
        .compact();
    },
    get imageUrl() {
      return quest.fussyAttestations.at(0)?.imageUrl;
    },
  };
}

const EmptyQuestProgress = (questId: number) => {
  return {
    id: 0,
    status: UserQuestProgressStatus.NotRequestedYet,
    questId,
  };
};

const EmptyQuestTaskProgress = (questTaskId: number) => {
  return {
    id: 0,
    status: UserQuestTaskProgressStatus.NotRequestedYet,
    questTaskId,
  };
};

export { EmptyQuestProgress, EmptyQuestTaskProgress, wrapQuest };
export type { Quest, Props, WrappedQuest };
